/**
 * This hook handles integrating React Query with an AntD Table and the URLSearchParams. It uses
 * the URLSearchParams as a source of truth for the state of the table, and when AntD calls the
 * onChange callback for filters/sorts/pagination, it updates the URLSearchParams.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { message, TableProps } from 'antd';
import { ColumnType, FilterValue, SorterResult, SortOrder } from 'antd/lib/table/interface';
// TODO: When we've updated webpack/babel, we should switch to change-case instead as these
// packages have been deprecated by the author
import { snakeCase } from 'snake-case';
import { useQuery, QueryOptions, UseQueryResult } from '@tanstack/react-query';
import { t } from 'ttag';
import { ParsedQs } from 'qs';

import useSyncWithUrlSearchParams from './useSyncWithUrlSearchParams';

const DEFAULT_PAGE_LIMIT = 20;

/*
  Detects if there are any filter on query URLSearchParams.
*/
export const hasAnyFilters = (query: URLSearchParams) => {
  const allParams = Array.from(query.entries());
  return allParams.some(([key]) => key.startsWith('filter[') || key.startsWith('search'));
};

/**
 * Use the URLSearchParams as the source of truth for the table state. This will convert the URL
 * parameters into a TrackedState object that is subsequently transformed into the correct formats
 * for the AntD Table's props and React Query APIs. One of the table props returned is the
 * `onChange` function that AntD's Table expects, which will synchronize those values to the URL,
 * which will then be derived into table state again.
 */
const useTableUrlSearchParams = <T>({
  searchValue = '',
  defaultPageLimit = DEFAULT_PAGE_LIMIT,
  defaultSort,
}: UseTableUrlSearchParams = {}): TableUrlSearchParams => {
  const [urlParams, setUrlParams] = useSyncWithUrlSearchParams();

  // Seed the URL with the default sort on initial mount when no sort param exists. This promotes
  // the default sort into a regular URL param so the normal ascend → descend → clear cycle works
  // without any special-casing. The ref bridges the first render (before the effect runs) so that
  // trackedState can provide the sort immediately without triggering a wasted API call.
  const defaultSortSeeded = useRef(false);
  useEffect(() => {
    if (!defaultSort || defaultSortSeeded.current || urlParams.sort) return;
    setUrlParams(prevState => {
      if (prevState.sort) return prevState;
      return {
        ...prevState,
        sort: `${defaultSort.order === 'descend' ? '-' : ''}${defaultSort.field}`,
      };
    });
    defaultSortSeeded.current = true;
  }, [defaultSort, setUrlParams, urlParams.sort]);

  const onTableChange = useCallback<RestrictedTableProps<T>['onChange']>(
    (nextPagination, nextFilter, nextSort, { action }) => {
      setUrlParams(prevState => {
        const nextState = { ...prevState };
        if (action === 'filter') {
          nextState.filter = Object.entries(nextFilter).reduce((acc, [key, values]) => {
            if (values?.length > 0) {
              acc[key] = values.join(',');
            }
            return acc;
          }, {});
          // When the filter is changed, reset the current page
          delete nextState.page;
          return nextState;
        }

        if (action === 'sort') {
          if (Array.isArray(nextSort)) {
            throw new Error('Multiple column sorting is not supported');
          }
          if (typeof nextSort.order === 'undefined') {
            delete nextState.sort;
          } else {
            nextState.sort = `${nextSort.order === 'descend' ? '-' : ''}${
              nextSort.columnKey || nextSort.field
            }`;
          }
          // When the sort is changed, reset the current page
          delete nextState.page;
          return nextState;
        }

        if (action === 'paginate') {
          nextState.page = `${nextPagination.current}`;
          nextState.limit = `${nextPagination.pageSize}`;
          return nextState;
        }

        // This should never be reached, but guards against a future action being added in AntD
        return nextState;
      });
    },
    [setUrlParams],
  );

  // When the search value changes, update the URL and reset the current page
  useEffect(() => {
    setUrlParams(prevState => {
      const nextState: ParsedQs = {
        ...prevState,
        search: searchValue !== '' ? searchValue : undefined,
      };
      if ((searchValue || '') !== (prevState.search || '')) {
        delete nextState.page;
      }
      return nextState;
    });
  }, [setUrlParams, searchValue]);

  // Parse the current URL into a TrackedState object
  const trackedState = useMemo<TrackedState>(() => {
    const parsed: TrackedState = {
      pagination: {
        // If either of these are not in the urlParams or are not numbers, they will be parsed
        // as `NaN || ...` which will result in the correct default values.
        limit: Number(urlParams.limit) || defaultPageLimit,
        // AntD uses 1-based indexing for pages, but we use 0-based indexing. We default the URL
        // parameter and subtract 1 from it to ensure consistency regardless of whether the page
        // is set in the URL
        page: (Number(urlParams.page) || 1) - 1,
      },
    };

    if (urlParams.filter) {
      parsed.filter = Object.fromEntries(
        Object.entries(urlParams.filter).map(([key, value]) => [key, (value as string).split(',')]),
      );
    }

    if (urlParams.sort) {
      if (typeof urlParams.sort !== 'string') {
        throw new Error('Multiple column sorting is not supported');
      }
      const order: SortOrder = urlParams.sort.startsWith('-') ? 'descend' : 'ascend';
      const field = order === 'descend' ? urlParams.sort.slice(1) : urlParams.sort;
      parsed.sort = { field, order };
    } else if (defaultSort && !defaultSortSeeded.current) {
      // Provide the default sort for the initial render, before the effect has seeded the URL.
      // After seeding, the sort is a regular URL param and this branch is never reached again.
      parsed.sort = { field: defaultSort.field, order: defaultSort.order };
    }

    if (urlParams.search) {
      parsed.search = urlParams.search as string;
    }

    return parsed;
  }, [urlParams, defaultPageLimit, defaultSort]);

  const tableProps = useMemo<TableUrlSearchParams['tableProps']>(
    () => ({
      onChange: onTableChange,
      pagination: {
        pageSize: trackedState.pagination.limit,
        // AntD uses 1-based indexing for pages, but we use 0-based indexing
        current: trackedState.pagination.page + 1,
      },
    }),
    [onTableChange, trackedState.pagination],
  );

  return {
    tableProps,
    trackedState,
  };
};

export interface UseTableUrlSearchParams {
  /**
   * If a table has a search field, the value can be passed in here. Whenever this value changes,
   * the URL will be updated and the table will return to the first page. This does not provide a
   * mechanism for getting the current value, and that must be provided by the parent component.
   */
  searchValue?: string;
  /**
   * By default, the page limit will be 20 if pagination is disabled and 1000 if pagination is
   * disabled. If you want to use a different page size by default, you can provide a value here.
   * This will not force the page size, and if the pagination size is configurable in the table,
   * or if a user sets `?limit=...` in the URL, that will take precedence.
   */
  defaultPageLimit?: number;
  /**
   * If provided, this sort will be applied when no sort parameter is present in the URL. This is
   * useful for tables that should have a meaningful default sort order on initial load.
   */
  defaultSort?: {
    field: string;
    order: 'ascend' | 'descend';
  };
}

/**
 * AntD Tables allow multi-column sorting by default, but we don't use that functionality in our
 * tables. This type restricts the sorter to only allow a single column to be sorted at a time,
 * which simplifies the logic for transforming the sorter to/from a URL parameter.
 */
export interface RestrictedSorterResult<T> extends SorterResult<T> {
  field: string;
}

/**
 * The TrackedState object is the source of truth for the table state. It is derived from the
 * URLSearchParams, and provides a more structured way to access the table state than the
 * URLSearchParams object itself. This object is used to derive the table props and the query
 * parameters for the API.
 */
export interface TrackedState {
  filter?: Record<string, FilterValue>;
  sort?: RestrictedSorterResult<any>;
  search?: string;
  pagination: {
    limit: number;
    /**
     * This uses 0-based indexing to match what the API expects
     */
    page: number;
  };
}

/**
 * useTableUrlSearchParams returns the props for the Table component, as well as the tracked state
 * that is used to derive the query parameters for the API.
 */
export interface TableUrlSearchParams {
  tableProps: TableProps<any> & Required<Pick<TableProps<any>, 'onChange' | 'pagination'>>;
  trackedState: TrackedState;
}

/**
 * Call useTableQuery with your query and table options, and it will return everything required
 * for an AntD Table to be a controlled table, with querying, filtering, sorting, and pagination.
 * It does not handle any functionality that does not exist in an AntD table, such as search bars
 * or custom field filters, which can be added through some of the returned utility functions like
 * `transformApiParams`.
 */
export const useTableQuery = <T>(
  /**
   * A query options factory that will be called with the derived API parameters and the tracked
   * state. This should return the options that will be passed to `useQuery`, and expects that the
   * queryFn will return an APIResponsePayload object using camelCase keys.
   */
  getQueryOptions: TableQueryOptionsFactory<T>,
  /**
   * The props that would normally be passed to the AntD table. This is used to derive the columns
   * and pagination settings for the table. The columns will be updated with the filteredValue and
   * sortOrder from the tracked state, and the pagination will be updated with the totalItemsCount
   * from the API response.
   */
  sourceTableProps: RestrictedTableProps<T>,
  /**
   * If a search value is provided, it will be used to update the URLSearchParams and reset the
   * current page. This is useful for search bars that are outside of the table component.
   */
  searchValue?: string,
  /**
   * Additional options for configuring table behavior.
   */
  options?: {
    /** Sort to apply when no sort parameter is present in the URL. */
    defaultSort?: UseTableUrlSearchParams['defaultSort'];
  },
): UseTableQueryResult<T> => {
  // Pagination can be disabled and hidden by <Table pagination={false} /> or disabled and visible
  // by <Table pagination={{ disabled: true }} />. We never use the latter option, but we need to
  // account for it.
  const isPaginationDisabled =
    sourceTableProps?.pagination === false || sourceTableProps?.pagination?.disabled || false;
  // If pagination is disabled, and a page limit is not provided, default to 1000 so that all data
  // is loaded at once. If pagination is enabled, default to the provided size or fall back to 20.
  const defaultPageLimit =
    (sourceTableProps?.pagination && sourceTableProps.pagination.pageSize) ||
    (isPaginationDisabled ? 1000 : DEFAULT_PAGE_LIMIT);

  const { tableProps: tableUrlProps, trackedState } = useTableUrlSearchParams<T>({
    searchValue,
    defaultPageLimit,
    defaultSort: options?.defaultSort,
  });
  // Get a reference to the onChange function destined for AntD's table, since we'll use that when
  // we need to update the current page when there is no data available for a page.
  const { onChange } = tableUrlProps;

  // Derive the API parameters from the tracked state, since we need to use CSV for filters and
  // snake_case for sorts.
  const apiParams = useMemo(() => {
    const params: Record<string, string> = {
      limit: `${trackedState.pagination.limit}`,
      page: `${trackedState.pagination.page}`,
    };
    if (trackedState.filter) {
      Object.entries(trackedState.filter).forEach(([key, values]) => {
        if (values.length > 0) {
          params[key] = values.join(',');
        }
      });
    }
    if (trackedState.sort) {
      const snakeField = snakeCase(trackedState.sort.field);
      params.sort = `${trackedState.sort.order === 'descend' ? '-' : ''}${snakeField}`;
    }
    if (trackedState.search) {
      params.search = trackedState.search;
    }
    return params;
  }, [trackedState]);

  const queryResult = useQuery(getQueryOptions(apiParams, trackedState));

  // This is intentionally written this way to avoid rest destructuring of the query result
  // https://tanstack.com/query/latest/docs/eslint/no-rest-destructuring
  const { data, error, isLoading, isFetching } = queryResult;

  // When the data changes, we need to ensure that the response is using the camelCase key
  // transformer, and that there is data for the current page.
  useEffect(() => {
    if (isLoading || !data?.meta?.pagination) {
      return;
    }

    const { pagination } = data.meta;

    // Check if the returned data is using camelCase, since this will break all of the other table
    // logic as well
    if (
      typeof pagination.totalItemsCount === 'undefined' &&
      typeof (pagination as any).total_items_count !== 'undefined'
    ) {
      throw new Error('Called with a queryFn that returns snake_case, but expected camelCase');
    }

    // If there is no data on this page, move to the last page
    const maxPages = Math.floor(pagination.totalItemsCount / pagination.limit);
    if (pagination.pageItemsCount === 0 && pagination.page > maxPages) {
      // This mimics the behavior of AntD when changing the current page in the table.
      onChange(
        { current: maxPages + 1, pageSize: pagination.limit },
        {},
        {},
        { action: 'paginate', currentDataSource: data?.data || [] },
      );
    }
  }, [isLoading, data, onChange]);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      message.error(t`An unknown error occurred`);
    }
  }, [error]);

  // Update the columns we received with the filteredValue and sortOrder from the trackedState.
  // This ensures that the UX always reflects what is being tracked in the URL and queried. This
  // also allows us to use the derivedFilters function to set filter options based on the data,
  // like we do in the `Used By` column for organization email domains.
  const columns = useMemo(
    () =>
      sourceTableProps.columns.map(({ derivedFilters, ...column }) => {
        // Confirm that pagination is disabled, since we want to avoid showing filter values that
        // are based on a subset of the data (the current page).
        if (derivedFilters && !isPaginationDisabled) {
          throw new Error('Derived filters are only supported when pagination is disabled');
        }

        const columnId = column.key || (column as any).dataIndex;
        if (typeof column.defaultSortOrder !== 'undefined') {
          throw new Error(
            '`defaultSortOrder` as a column prop is not allowed. Use `options.defaultSort` in `useTableQuery` instead.',
          );
        }
        if (!columnId) {
          return column;
        }
        const result = {
          ...column,
          filteredValue: trackedState.filter?.[columnId] || [],
          sortOrder: trackedState.sort?.field === columnId ? trackedState.sort.order : null,
        };
        if (derivedFilters) {
          result.filters = derivedFilters(data?.data || []);
        }
        return result;
      }),
    [sourceTableProps.columns, trackedState, data, isPaginationDisabled],
  );

  // Merge in our query results with the tableProps
  const tableProps = useMemo<TableProps<T>>(
    () => ({
      ...sourceTableProps,
      ...tableUrlProps,
      pagination: !isPaginationDisabled && {
        ...(sourceTableProps.pagination || {}),
        ...tableUrlProps.pagination,
        total: data?.meta?.pagination?.totalItemsCount || 0,
      },
      columns,
      onChange,
      loading: isLoading || isFetching,
      dataSource: data?.data || [],
    }),
    [
      tableUrlProps,
      onChange,
      isLoading,
      isFetching,
      data,
      sourceTableProps,
      columns,
      isPaginationDisabled,
    ],
  );

  return {
    tableProps,
    query: queryResult,
  };
};

export type TableQueryOptionsFactory<T> = (
  params: Record<string, string>,
  state: TrackedState,
) => QueryOptions<APIResponsePayload<T[]>>;

export interface TableQueryColumn<T> extends ColumnType<T> {
  // Disable support for multisort
  sorter?: boolean;
  /**
   * A function that returns the filters for this column based on the rows that are currently
   * displayed. This is useful for columns that have a dynamic set of values that can be filtered.
   * This will only provide data for the current page, so it should be used when pagination is
   * disabled.
   * Warning: Since this derives from the current data, this will result in the filter only showing
   * options that are available in the currently filtered data too. As you apply filters, the other
   * options will disappear, which can be confusing to users.
   */
  derivedFilters?: (rows: T[]) => ColumnType<T>['filters'];
}

export interface RestrictedTableProps<T> extends TableProps<T> {
  // Redeclare the type to mark it as required and to only accept our custom column type that has
  // derived filters, and disabling column grouping and multisorting.
  columns: Required<Array<TableQueryColumn<T>>>;
}

export interface UseTableQueryResult<T> {
  tableProps: TableProps<T>;
  query: UseQueryResult<APIResponsePayload<T[]>, unknown>;
}
